import { useCallback, useContext, useMemo, useState } from 'react';

import {
  applyTranslationsToObject,
  extractTextsFromObject,
} from '@hotels-hotel-detail/utils/translation';
import UBFFContext from '@shared/data-access-frontend/contexts/ubff-context';
import { TranslationState } from '@shared/utils-frontend/translate-state';

const useTranslation = <T>({
  contentType,
  data,
  excludedFields = [],
  sourceLocale,
  targetLocale,
}: {
  data: T;
  sourceLocale: string;
  targetLocale: string;
  contentType: string;
  excludedFields: string[];
}) => {
  const [state, setState] = useState(TranslationState.ORIGINAL);
  const [translatedData, setTranslatedData] = useState<T | null>(null);
  const [hasTranslated, setHasTranslated] = useState(false);
  const uBFFGateway = useContext(UBFFContext);

  const extractTexts = useCallback(
    (obj: any) => extractTextsFromObject(obj, excludedFields),
    [excludedFields],
  );

  const translate = useCallback(async () => {
    if (state === TranslationState.TRANSLATED) {
      setState(TranslationState.ORIGINAL);
      return;
    }

    if (state === TranslationState.ORIGINAL && hasTranslated) {
      setState(TranslationState.TRANSLATED);
      return;
    }

    setState(TranslationState.PENDING);

    const { pathMap, texts } = extractTexts(data);

    if (texts.length === 0) {
      setState(TranslationState.ORIGINAL);
      return;
    }

    const { error, response } = await uBFFGateway.getTranslation({
      translationInputs: [
        {
          sourceLocale,
          sourceTexts: texts,
          contentType,
        },
      ],
      targetLocale,
    });

    if (error) {
      setState(TranslationState.FAILED);
      return;
    }

    if (
      response?.translatedContents?.[0]?.translatedTexts &&
      response.translatedContents[0].translatedTexts.length > 0
    ) {
      const { translatedTexts } = response.translatedContents[0];
      const translated = applyTranslationsToObject(
        data,
        pathMap,
        translatedTexts,
      );

      setTranslatedData(translated);
      setHasTranslated(true);
      setState(TranslationState.TRANSLATED);
      return;
    }

    setState(TranslationState.ORIGINAL);
  }, [
    state,
    hasTranslated,
    extractTexts,
    data,
    uBFFGateway,
    sourceLocale,
    contentType,
    targetLocale,
  ]);

  const currentData = useMemo(
    () =>
      state === TranslationState.TRANSLATED && translatedData
        ? translatedData
        : data,
    [state, translatedData, data],
  );

  return {
    state,
    isOriginal: state === TranslationState.ORIGINAL,
    isPending: state === TranslationState.PENDING,
    isTranslated: state === TranslationState.TRANSLATED,
    isFailed: state === TranslationState.FAILED,
    originalData: data,
    translatedData,
    currentData,
    translate,
  };
};

export default useTranslation;
